import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_PADDING, layoutTitle } from "@/lib/tool-kit/charts/layout";
import {
  type StemLeafDisplay,
  stemLabel,
} from "@/lib/tool-kit/charts/stem-leaf";
import {
  CHART_FONT,
  CHART_MONO_FONT,
  estimateTextWidth,
  MONO_CHAR_WIDTH,
} from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A stem-and-leaf display drawn as an image: monospace digits in columns, a rule
 * between stems and leaves, the key underneath. Back to back, the first data set's
 * leaves run leftward from the stems so both sides increase away from the middle.
 *
 * The digits are the data, so they are drawn in a monospace face; the title and key
 * use the charts' sans-serif.
 */

const SIZE = 15;
const CHAR = SIZE * MONO_CHAR_WIDTH;
const LINE = 22;

function leafText(leaves: number[]): string {
  return leaves.join(" ");
}

export function StemLeafChart({
  id,
  display,
  headings,
  keyText,
  title,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  display: StemLeafDisplay;
  /** [stem, leaf], or back to back [left set, stem, right set]. */
  headings: readonly string[];
  /** "Key: 3 | 7 = 37" — drawn under the display. */
  keyText: string;
  /** The title drawn in the image; empty for an APA figure. */
  title: string;
  label: string;
  description: string;
}) {
  const backToBack = display.rows[0]?.leaves.length === 2;
  const stems = display.rows.map((row) => stemLabel(row));
  const left = backToBack
    ? display.rows.map((row) => leafText([...row.leaves[0]].reverse()))
    : [];
  const right = display.rows.map((row) => leafText(row.leaves.at(-1) ?? []));
  const stemHeading = backToBack ? headings[1] : headings[0];
  const rightHeading = backToBack ? headings[2] : headings[1];
  const chars = (texts: readonly string[]) =>
    Math.max(0, ...texts.map((text) => [...text].length));
  const leftWidth = backToBack ? chars([...left, headings[0] ?? ""]) * CHAR : 0;
  const stemWidth = chars([...stems, stemHeading ?? ""]) * CHAR;
  const rightWidth = chars([...right, rightHeading ?? ""]) * CHAR;
  const gap = CHAR * 1.5;
  const leftEdge = CHART_PADDING + 8;
  const leftRule = leftEdge + leftWidth + (backToBack ? gap : 0);
  const stemStart = backToBack ? leftRule + gap : leftEdge;
  const stemRule = stemStart + stemWidth + gap;
  const rightStart = stemRule + gap;
  const keyWidth = estimateTextWidth(keyText, { fontSize: 12 });
  const width = Math.max(
    360,
    rightStart + rightWidth + CHART_PADDING + 8,
    leftEdge + keyWidth + CHART_PADDING
  );
  const heading = title.trim() ? layoutTitle(title, width) : null;
  const top = CHART_PADDING + (heading?.height ?? 0) + SIZE;
  const bodyTop = top + LINE * 0.6;
  const bottom = bodyTop + display.rows.length * LINE;
  const height = bottom + LINE * 1.6 + CHART_PADDING;
  const rule = (x: number) => (
    <line
      stroke={CHART_INK.text}
      strokeWidth={1.25}
      x1={px(x)}
      x2={px(x)}
      y1={px(top - SIZE)}
      y2={px(bottom - LINE * 0.4)}
    />
  );
  return (
    <ChartSvg
      description={description}
      fontFamily={CHART_MONO_FONT}
      height={height}
      id={id}
      label={label}
      width={width}
    >
      {heading ? (
        <g fontFamily={CHART_FONT}>
          <ChartTitle title={heading} />
        </g>
      ) : null}
      <g
        fill={CHART_INK.muted}
        fontFamily={CHART_FONT}
        fontSize={12}
        fontWeight="bold"
      >
        {backToBack ? (
          <text textAnchor="end" x={px(leftRule - gap)} y={px(top - 6)}>
            {headings[0]}
          </text>
        ) : null}
        <text
          textAnchor={backToBack ? "middle" : "end"}
          x={px(backToBack ? stemStart + stemWidth / 2 : stemStart + stemWidth)}
          y={px(top - 6)}
        >
          {stemHeading}
        </text>
        <text x={px(rightStart)} y={px(top - 6)}>
          {rightHeading}
        </text>
      </g>
      <line
        stroke={CHART_INK.grid}
        strokeWidth={1}
        x1={px(leftEdge)}
        x2={px(width - CHART_PADDING)}
        y1={px(top + 2)}
        y2={px(top + 2)}
      />
      {backToBack ? rule(leftRule) : null}
      {rule(stemRule)}
      <g fill={CHART_INK.text} fontSize={SIZE}>
        {stems.map((stem, index) => {
          const y = bodyTop + (index + 1) * LINE - LINE * 0.3;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are stems in value order
            <g key={index}>
              {backToBack ? (
                <text textAnchor="end" x={px(leftRule - gap)} y={px(y)}>
                  {left[index]}
                </text>
              ) : null}
              <text
                textAnchor={backToBack ? "middle" : "end"}
                x={px(
                  backToBack ? stemStart + stemWidth / 2 : stemStart + stemWidth
                )}
                y={px(y)}
              >
                {stem}
              </text>
              <text x={px(rightStart)} y={px(y)}>
                {right[index]}
              </text>
            </g>
          );
        })}
      </g>
      <text
        fill={CHART_INK.text}
        fontFamily={CHART_FONT}
        fontSize={12}
        x={px(leftEdge)}
        y={px(bottom + LINE)}
      >
        {keyText}
      </text>
    </ChartSvg>
  );
}
